import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  LinearProgress,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import LaunchRoundedIcon from "@mui/icons-material/LaunchRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import { insetSx, surfaceSx } from "../center/ownerCenterUi";

const STAGE = {
  implementation: { label: "En implementación", tone: "warning" },
  delivered: { label: "Entregado", tone: "info" },
  operating: { label: "Operando", tone: "success" },
  inactive: { label: "Inactivo", tone: "default" },
};

function number(value) {
  return new Intl.NumberFormat("es-AR", { maximumFractionDigits: 0 }).format(Number(value || 0));
}

function money(value, currency = "ARS") {
  try {
    return new Intl.NumberFormat("es-AR", { style: "currency", currency, maximumFractionDigits: 0 }).format(Number(value || 0));
  } catch {
    return `${number(value)} ${currency}`;
  }
}

function dateTime(value) {
  if (!value) return "Sin registro";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Sin registro";
  return new Intl.DateTimeFormat("es-AR", { dateStyle: "short", timeStyle: "short" }).format(date);
}

function relative(value) {
  if (!value) return "Nunca registrado";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Nunca registrado";
  const diff = Date.now() - date.getTime();
  const minutes = Math.max(0, Math.round(diff / 60000));
  if (minutes < 2) return "recién";
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `hace ${hours} h`;
  const days = Math.round(hours / 24);
  return `hace ${days} día${days === 1 ? "" : "s"}`;
}

function Metric({ label, value, hint }) {
  return <Box sx={{ ...insetSx, p: 1.05, minWidth: 0 }}>
    <Typography sx={{ color: "text.secondary", fontSize: ".62rem", fontWeight: 950, textTransform: "uppercase", letterSpacing: ".045em" }}>{label}</Typography>
    <Typography sx={{ mt: .15, fontSize: "1.05rem", fontWeight: 1000, lineHeight: 1.15 }}>{value}</Typography>
    {hint ? <Typography sx={{ mt: .2, color: "text.secondary", fontSize: ".65rem", lineHeight: 1.25 }}>{hint}</Typography> : null}
  </Box>;
}

export default function OwnerProjectWebProfitHealthPanel({ health, loading, error, onRetry, onEditProject, compact = false }) {
  if (loading) return compact
    ? <Box data-owner-jarvis-section="project-webprofit" sx={{ ...surfaceSx, p: 1.05 }}><Skeleton height={26} width="55%" /></Box>
    : <Box data-owner-jarvis-section="project-webprofit" sx={{ ...surfaceSx, p: 1.4 }}><Skeleton height={28} width="35%" /><Skeleton variant="rounded" height={120} sx={{ mt: .7 }} /></Box>;
  if (error) return compact
    ? <Box sx={{ ...surfaceSx, p: 1.05 }}><Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}><Typography sx={{ color: "text.secondary", fontSize: ".72rem" }}>No pude leer el estado de WebProfit.</Typography><Button size="small" onClick={onRetry} sx={{ textTransform: "none", fontWeight: 900 }}>Reintentar</Button></Stack></Box>
    : <Alert severity="warning" action={<Button size="small" onClick={onRetry} sx={{ textTransform: "none", fontWeight: 900 }}>Reintentar</Button>}>{error}</Alert>;
  if (!health?.linked) {
    return <Box data-owner-jarvis-section="project-webprofit" sx={{ ...surfaceSx, p: compact ? 1.05 : 1.4 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
        <Box><Typography sx={{ fontWeight: 1000, fontSize: compact ? ".8rem" : ".88rem" }}>WebProfit sin vincular</Typography><Typography sx={{ color: "text.secondary", fontSize: compact ? ".66rem" : ".72rem" }}>{compact ? "Vinculalo para sumar adopción y actividad al estado del proyecto." : "Vinculá el Project WebProfit entregado para medir uso, visitas, contactos y adopción."}</Typography></Box>
        <Button size="small" variant="outlined" onClick={onEditProject} sx={{ textTransform: "none", fontWeight: 900, flexShrink: 0 }}>Vincular</Button>
      </Stack>
    </Box>;
  }

  const project = health.project || {};
  const adoption = health.adoption || {};
  const stage = STAGE[adoption.stage] || STAGE.implementation;
  const storefront = health.storefront || {};
  const panel = health.panel || {};
  const orders = health.orders || {};
  const clientUsage = health.clientUsage || {};
  const days = Number(health.period?.days || 30);
  const score = Math.max(0, Math.min(100, Number(adoption.score || 0)));

  if (compact) {
    return <Box data-owner-jarvis-section="project-webprofit" sx={{ ...surfaceSx, p: 1.05 }}>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={.75} alignItems={{ sm: "center" }}>
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={.45} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography sx={{ fontWeight: 1000, fontSize: ".8rem" }}>WebProfit · {project.name}</Typography>
            <Chip size="small" color={stage.tone} label={stage.label} sx={{ height: 20, fontWeight: 900, fontSize: ".61rem" }} />
            <Chip size="small" variant="outlined" label={`Adopción ${score}%`} sx={{ height: 20, fontWeight: 900, fontSize: ".61rem" }} />
          </Stack>
          <Typography sx={{ mt: .28, color: "text.secondary", fontSize: ".66rem", lineHeight: 1.35 }}>
            Último uso {relative(clientUsage.lastUseAt)} · {number(storefront.pageViews)} visitas / {days}d · {number(orders.orders)} pedidos
          </Typography>
        </Box>
        {project.links?.projectHome ? <Button size="small" href={project.links.projectHome} startIcon={<LaunchRoundedIcon />} sx={{ flexShrink: 0, textTransform: "none", fontWeight: 900 }}>Abrir WebProfit</Button> : null}
      </Stack>
      <LinearProgress variant="determinate" value={score} sx={{ mt: .65, height: 5, borderRadius: 999 }} />
    </Box>;
  }

  return <Box data-owner-jarvis-section="project-webprofit" sx={{ ...surfaceSx, p: { xs: 1.2, md: 1.4 } }}>
    <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1} alignItems={{ md: "center" }}>
      <Stack direction="row" spacing={.6} alignItems="center" flexWrap="wrap" useFlexGap>
        <StorefrontRoundedIcon sx={{ fontSize: 19 }} />
        <Typography sx={{ fontWeight: 1000, fontSize: ".9rem" }}>Producto WebProfit · {project.name}</Typography>
        <Chip size="small" color={stage.tone} label={stage.label} sx={{ height: 22, fontWeight: 900, fontSize: ".64rem" }} />
        <Chip size="small" variant="outlined" label={`Adopción ${score}%`} sx={{ height: 22, fontWeight: 900, fontSize: ".64rem" }} />
      </Stack>
      <Stack direction="row" spacing={.45} flexWrap="wrap" useFlexGap>
        {project.links?.projectHome ? <Button size="small" href={project.links.projectHome} startIcon={<LaunchRoundedIcon />} sx={{ textTransform: "none", fontWeight: 900 }}>Abrir WebProfit</Button> : null}
        {project.links?.projectLanding ? <Button size="small" href={project.links.projectLanding} startIcon={<EditRoundedIcon />} sx={{ textTransform: "none", fontWeight: 900 }}>Página</Button> : null}
        {project.links?.projectStats ? <Button size="small" href={project.links.projectStats} startIcon={<InsightsRoundedIcon />} sx={{ textTransform: "none", fontWeight: 900 }}>Estadísticas</Button> : null}
      </Stack>
    </Stack>

    <Box sx={{ mt: .8 }}>
      <LinearProgress variant="determinate" value={score} sx={{ height: 7, borderRadius: 999 }} />
      <Typography sx={{ mt: .35, color: "text.secondary", fontSize: ".65rem" }}>{(adoption.factors || []).slice(0, 5).join(" · ") || "Todavía no hay señales suficientes de adopción."}</Typography>
    </Box>

    <Box sx={{ mt: 1, display: "grid", gridTemplateColumns: { xs: "repeat(2,minmax(0,1fr))", sm: "repeat(3,minmax(0,1fr))", lg: "repeat(6,minmax(0,1fr))" }, gap: .65 }}>
      <Metric label={`Visitas · ${days}d`} value={number(storefront.pageViews)} hint={`${number(storefront.uniqueVisitors)} visitantes`} />
      <Metric label="Contactos" value={number(storefront.contactClicks)} hint={`${number(storefront.whatsappClicks)} por WhatsApp`} />
      <Metric label="Carritos" value={number(storefront.cartAdds)} hint={`${number(storefront.checkouts)} checkout`} />
      <Metric label="Pedidos" value={number(orders.orders)} hint={orders.revenue ? money(orders.revenue, orders.currency) : "Sin ventas registradas"} />
      <Metric label="Ediciones página" value={number(panel.landingEdits)} hint={`${number(panel.sessions)} sesiones de panel`} />
      <Metric label="Preparación" value={`${number(adoption.readinessScore)}%`} hint={project.deployment?.status ? `Deploy ${project.deployment.status}` : "Sin deploy informado"} />
    </Box>

    <Box sx={{ mt: .8, display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3,minmax(0,1fr))" }, gap: .65 }}>
      <Box sx={{ ...insetSx, p: 1 }}><Typography sx={{ fontWeight: 950, fontSize: ".72rem" }}>Último uso del cliente</Typography><Typography sx={{ mt: .18, fontSize: ".82rem", fontWeight: 900 }}>{relative(clientUsage.lastUseAt)}</Typography><Typography sx={{ color: "text.secondary", fontSize: ".64rem" }}>{clientUsage.linkedUser ? `${clientUsage.linkedUser.name || clientUsage.linkedUser.email} · ${dateTime(clientUsage.lastUseAt)}` : "Vinculá también el usuario WebProfit del cliente para medir su uso personal."}</Typography></Box>
      <Box sx={{ ...insetSx, p: 1 }}><Typography sx={{ fontWeight: 950, fontSize: ".72rem" }}>Última edición de página</Typography><Typography sx={{ mt: .18, fontSize: ".82rem", fontWeight: 900 }}>{relative(panel.lastLandingEditAt)}</Typography><Typography sx={{ color: "text.secondary", fontSize: ".64rem" }}>{dateTime(panel.lastLandingEditAt)}</Typography></Box>
      <Box sx={{ ...insetSx, p: 1 }}><Typography sx={{ fontWeight: 950, fontSize: ".72rem" }}>Última actividad pública</Typography><Typography sx={{ mt: .18, fontSize: ".82rem", fontWeight: 900 }}>{relative(storefront.lastActivityAt)}</Typography><Typography sx={{ color: "text.secondary", fontSize: ".64rem" }}>{dateTime(storefront.lastActivityAt)}</Typography></Box>
    </Box>
  </Box>;
}
